/*
  Cart promotion blocks runtime (C1): <mv-cart-progress> (Progress bar / Checkpoints bar) and
  <mv-cart-gift> (Free / Conditional gift). Loaded by the blocks; sets itself up once.

  Inside the cart drawer / a cart page section the blocks are re-rendered by the server after
  every cart change (pC-contracts.md §2), so these elements only:
    - mv-cart-progress: recompute the bar and message from the `mv:cart-change` cart JSON when the
      block sits outside a repainted mount (nothing to do otherwise).
    - mv-cart-gift: reconcile the cart with the block's server-computed state on every connect
      (first paint and every repaint): unlocked → the chosen variant is in the cart once; locked →
      its lines are removed. All mutations go through Multiverse.cart (source 'mv-cart-gift');
      reconciliations are serialised per block, a disconnected element never mutates (the fresh
      one will), and a variant that Shopify refuses (or that does not stick) is not retried on the
      page, so the sync can never loop. The variant picker's choice lives in sessionStorage.
*/
(() => {
  window.Multiverse = window.Multiverse || {};
  const MV = window.Multiverse;
  if (MV.cartPromoRuntimeLoaded) return;
  MV.cartPromoRuntimeLoaded = true;

  const define = (name, cls) => {
    if (MV.defineOnce) MV.defineOnce(name, cls);
    else if (!customElements.get(name)) customElements.define(name, cls);
  };
  const parseJSON = (text, fallback) => {
    try {
      return JSON.parse(text);
    } catch (e) {
      return fallback;
    }
  };
  const escapeHTML = (text) =>
    String(text).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  // Shopify money_format renderer for the client-side fallback ({{amount}} & friends);
  // trailing ".00" dropped like money_without_trailing_zeros.
  const formatMoney = (cents, format) => {
    if (typeof MV.formatMoney === 'function') {
      try {
        return MV.formatMoney(cents, format, { trim: true });
      } catch (e) {
        /* fall through */
      }
    }
    const pattern = format || '${{amount}}';
    const match = /\{\{\s*(\w+)\s*\}\}/.exec(pattern);
    const kind = match ? match[1] : 'amount';
    const value = Math.round(cents) / 100;
    const group = (whole, sep) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    let text;
    switch (kind) {
      case 'amount_no_decimals':
        text = group(String(Math.round(value)), ',');
        break;
      case 'amount_with_comma_separator': {
        const [whole, frac] = value.toFixed(2).split('.');
        text = `${group(whole, '.')},${frac}`;
        break;
      }
      case 'amount_no_decimals_with_comma_separator':
        text = group(String(Math.round(value)), '.');
        break;
      case 'amount_with_apostrophe_separator': {
        const [whole, frac] = value.toFixed(2).split('.');
        text = `${group(whole, "'")}.${frac}`;
        break;
      }
      default: {
        const [whole, frac] = value.toFixed(2).split('.');
        text = `${group(whole, ',')}.${frac}`;
      }
    }
    text = text.replace(/[.,]00$/, '');
    return pattern.replace(/\{\{\s*\w+\s*\}\}/, text);
  };

  const cartLines = (cart) => (cart && Array.isArray(cart.items) ? cart.items : []);

  // ---- mv-cart-progress ----------------------------------------------------------------------
  class CartProgress extends HTMLElement {
    connectedCallback() {
      this._onChange = (event) => this.onCartChange(event.detail);
      document.addEventListener('mv:cart-change', this._onChange);
    }

    disconnectedCallback() {
      document.removeEventListener('mv:cart-change', this._onChange);
    }

    onCartChange(detail) {
      if (!this.isConnected || !detail || !detail.cart) return;
      // A block inside a re-rendered mount was (or is being) repainted by the server.
      if (this.closest('[data-mv-cart-section]')) return;
      this.update(detail.cart);
    }

    currentValue(cart) {
      if (this.dataset.goalType === 'quantity') {
        const hidden = new Set((this.dataset.hiddenVariants || '').split(',').filter(Boolean).map(Number));
        return cartLines(cart).reduce((sum, line) => sum + (hidden.has(line.variant_id) ? 0 : line.quantity), 0);
      }
      return typeof cart.total_price === 'number' ? cart.total_price : 0;
    }

    update(cart) {
      const goals = parseJSON(this.dataset.goals || '[]', []).filter((g) => g && typeof g.amount === 'number');
      if (!goals.length) return;
      const current = this.currentValue(cart);
      const highest = Math.max(...goals.map((g) => g.amount));
      const autoComplete = this.dataset.autoComplete === 'true';
      const next = goals.filter((g) => g.amount > current).sort((a, b) => a.amount - b.amount)[0] || null;
      const complete = autoComplete || !next;
      const percent = complete || highest <= 0 ? 100 : Math.min(100, (current / highest) * 100);
      this.dataset.current = String(current);
      this.classList.toggle('is-complete', complete);
      const fill = this.querySelector('[data-mv-progress-fill]');
      if (fill) fill.style.width = `${percent}%`;
      const track = this.querySelector('[role="progressbar"]');
      if (track) track.setAttribute('aria-valuenow', String(Math.round(percent)));
      this.querySelectorAll('[data-mv-goal]').forEach((goal) => {
        goal.classList.toggle('is-reached', current >= Number(goal.dataset.mvGoal));
      });
      const text = this.querySelector('[data-mv-progress-text]');
      if (!text) return;
      if (complete) {
        text.innerHTML = this.dataset.successMessage || '';
        return;
      }
      const remaining = Math.max(0, next.amount - current);
      const amount =
        this.dataset.goalType === 'quantity'
          ? String(remaining)
          : `<span class="mv-progress__amount">${escapeHTML(formatMoney(remaining, this.dataset.moneyFormat))}</span>`;
      const goalText = next.text || '';
      text.innerHTML = (this.dataset.progressMessage || '')
        .split('[amount]')
        .join(amount)
        .split('[next_goal_lowercase]')
        .join(escapeHTML(goalText.toLowerCase()))
        .split('[next_goal]')
        .join(escapeHTML(goalText));
    }
  }
  define('mv-cart-progress', CartProgress);

  // ---- mv-cart-gift --------------------------------------------------------------------------
  const SOURCE = 'mv-cart-gift';
  const STORAGE_PREFIX = 'mv-cart-gift-';
  const chains = new Map(); // block id → promise chain (one reconciliation at a time per block)
  const refused = new Set(); // `${blockId}:${variantId}` that Shopify refused / did not keep
  const attempts = new Map(); // `${blockId}:${variantId}` → adds sent on this page

  const readChoice = (blockId) => {
    try {
      return Number(window.sessionStorage.getItem(STORAGE_PREFIX + blockId)) || 0;
    } catch (e) {
      return 0;
    }
  };
  const writeChoice = (blockId, variantId) => {
    try {
      window.sessionStorage.setItem(STORAGE_PREFIX + blockId, String(variantId));
    } catch (e) {
      /* private mode */
    }
  };

  class CartGift extends HTMLElement {
    connectedCallback() {
      this.blockId = this.dataset.blockId || '';
      this.productId = Number(this.dataset.productId) || 0;
      this.variantId = Number(this.dataset.variantId) || 0;
      this.variants = parseJSON(this.dataset.variants || '[]', []);
      this.passive = this.hasAttribute('data-passive');
      this.unlocked = this.dataset.unlocked === 'true';
      this.selects = [...this.querySelectorAll('[data-mv-gift-option]')];
      this._onChange = (event) => {
        if (event.target.matches && event.target.matches('[data-mv-gift-option]')) this.onOptionChange();
      };
      this.addEventListener('change', this._onChange);
      this.restoreChoice();
      // The drawer repaint that connected this element is still finishing (the controller reads
      // the fresh cart JSON right after the innerHTML swap): reconcile once it has settled.
      this._timer = window.setTimeout(() => {
        if (MV.cart) this.sync();
        else window.addEventListener('load', () => this.isConnected && this.sync(), { once: true });
      }, 0);
    }

    disconnectedCallback() {
      window.clearTimeout(this._timer);
      this.removeEventListener('change', this._onChange);
    }

    variantById(id) {
      return this.variants.find((v) => Number(v.id) === Number(id)) || null;
    }

    // The customer's earlier pick (sessionStorage) wins over the server's first available variant.
    restoreChoice() {
      const stored = readChoice(this.blockId);
      if (!stored || stored === this.variantId) return;
      const variant = this.variantById(stored);
      if (!variant || variant.available === false) return;
      this.variantId = stored;
      this.dataset.variantId = String(stored);
      if (Array.isArray(variant.options)) {
        this.selects.forEach((select, index) => {
          if (variant.options[index] !== undefined) select.value = variant.options[index];
        });
      }
    }

    onOptionChange() {
      const values = this.selects.map((select) => select.value);
      const variant = this.variants.find(
        (v) => Array.isArray(v.options) && v.options.length === values.length && v.options.every((o, i) => o === values[i])
      );
      if (!variant || variant.available === false) {
        this.classList.add('mv-cart-gift--unavailable');
        return;
      }
      this.classList.remove('mv-cart-gift--unavailable');
      this.variantId = Number(variant.id);
      this.dataset.variantId = String(variant.id);
      writeChoice(this.blockId, variant.id);
      this.sync();
    }

    // Serialised per block: a reconciliation started by a stale element finishes before the fresh
    // one reads the cart.
    sync() {
      const key = this.blockId || 'gift';
      const run = (chains.get(key) || Promise.resolve()).then(() => this.reconcile()).catch(() => {});
      chains.set(key, run);
      return run;
    }

    async currentCart() {
      if (!MV.cart) return null;
      const panel = this.closest('[data-mv-cart-section]');
      const script = panel ? panel.querySelector('script[data-mv-cart-json]') : null;
      const embedded = script ? parseJSON(script.textContent, null) : null;
      const state = typeof MV.cart.getState === 'function' ? MV.cart.getState() : null;
      if (state) return state;
      if (embedded) return embedded;
      return typeof MV.cart.get === 'function' ? MV.cart.get() : null;
    }

    async reconcile() {
      if (!this.isConnected || !MV.cart || !this.productId) return;
      const cart = await this.currentCart();
      if (!cart || !this.isConnected) return;
      const lines = cartLines(cart).filter((line) => line.product_id === this.productId);
      if (this.passive && this.unlocked) return; // earned elsewhere: leave it alone
      if (!this.unlocked) {
        for (const line of lines) {
          if (!this.isConnected) return;
          await MV.cart.change(line.key, 0, { source: SOURCE });
        }
        return;
      }
      const want = this.variantId;
      const keep = lines.find((line) => line.variant_id === want) || null;
      for (const line of lines) {
        if (line === keep) continue;
        if (!this.isConnected) return;
        await MV.cart.change(line.key, 0, { source: SOURCE });
      }
      const key = `${this.blockId}:${want}`;
      if (keep) {
        attempts.delete(key); // the add stuck: later legitimate re-adds start from zero
        if (keep.quantity !== 1 && this.isConnected) await MV.cart.change(keep.key, 1, { source: SOURCE });
        return;
      }
      if (!want || refused.has(key) || !this.isConnected) return;
      const sent = (attempts.get(key) || 0) + 1;
      attempts.set(key, sent);
      if (sent > 2) {
        refused.add(key); // added twice and never seen in the cart: stop
        return;
      }
      try {
        await MV.cart.add({ id: want, quantity: 1, properties: { _mv_gift: 'true' } }, { source: SOURCE, open: false });
        this.dispatchEvent(new CustomEvent('mv:gift-added', { bubbles: true, detail: { blockId: this.blockId, variantId: want } }));
      } catch (error) {
        refused.add(key);
      }
    }
  }
  define('mv-cart-gift', CartGift);
})();
